import { useCallback, useEffect, useRef, useState } from 'react';
import type { RenderNodesMap } from '../types/commontypes';
import type { RenderNode } from '../types/commontypes';
import { ShapeType } from '../types/commontypes';
import { entries } from '../utils/utils';
import type { StorageShapeInput, StorageShapesInputMap } from '@/types/shape';
import { nodeToStorageShape, storageToRenderNode } from '../utils/shapeConverter';
import type { StockData } from '../types/chart';
import type { XAccessor } from './use-candle-data';

/**
 * localStorage 저장 시: RenderNode → StorageShapesInputMap(간결한 저장 형태)로 변환.
 * Measure 도형은 저장 대상에서 제외.
 * 빈 배열인 shape 그룹은 저장하지 않음.
 *
 * id는 미발급 상태일 수 있으므로 StorageShapeInput 기반.
 * 좌표는 `nodeToStorageShape`로 재조립만 수행한다 (index→timestamp 변환 없음).
 */
export const sanitizeForStorage = (data: RenderNodesMap): StorageShapesInputMap => {
  const sanitized: StorageShapesInputMap = {};

  entries(data).forEach(([chartId, chartShapes]) => {
    const chartData: Record<string, StorageShapeInput[]> = {};

    entries(chartShapes).forEach(([type, shapes]) => {
      // Skip Measure shapes so they are not persisted.
      if (type === ShapeType.MEASURE) return;
      if (!shapes || !Array.isArray(shapes)) return;
      if (shapes.length === 0) return;

      chartData[type] = shapes
        .map((node: RenderNode) => {
          // nodeToStorageShape는 MEASURE에 대해 throw하므로 사전 필터
          if (node.type === ShapeType.MEASURE) return null;
          try {
            return nodeToStorageShape(node);
          } catch (e) {
            console.error(`[sanitizeForStorage] Failed to convert ${type} node:`, node.type, node.uuid, e);
            return null;
          }
        })
        .filter((s): s is StorageShapeInput => s !== null);
    });

    if (Object.keys(chartData).length > 0) {
      sanitized[chartId] = chartData;
    }
  });

  return sanitized;
};

/**
 * localStorage 로드 시: StorageShapesInputMap(저장 형태) → RenderNode(렌더링 형태)로 변환.
 * candleData/timeframe/xAccessor가 필요하므로, 이들이 없으면 좌표 변환을 건너뛰고
 * StorageShape 데이터를 그대로 RenderNode의 최소 형태로 복원한다.
 */
export const restoreFromStorage = (
  data: StorageShapesInputMap,
  candleData: StockData[],
  timeframe: string,
  xAccessor?: XAccessor,
): RenderNodesMap => {
  const restored: RenderNodesMap = {};

  entries(data).forEach(([chartId, chartShapes]) => {
    if (!chartShapes || typeof chartShapes !== 'object') return;
    restored[chartId] = {};

    entries(chartShapes).forEach(([type, shapes]) => {
      if (!shapes || !Array.isArray(shapes)) return;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      (restored[chartId] as Record<ShapeType, RenderNode[]>)[type] = shapes.map((shape: StorageShapeInput) =>
        storageToRenderNode(shape, candleData, timeframe, xAccessor),
      );
    });
  });

  return restored;
};

/**
 * Hook for persisting renderNodes to localStorage
 *
 * Automatically saves renderNodes to localStorage whenever it changes,
 * and loads initial data from localStorage on mount.
 *
 * @param key - localStorage key (e.g., "shapes_BTC/USDT:USDT_1h")
 * @param initialValue - Default value if localStorage is empty
 * @param candleData - 캔들 데이터 (좌표 변환용, 로드 시 필요)
 * @param timeframe - 타임프레임 문자열 (좌표 변환용, 로드 시 필요)
 * @param xAccessor - ScaleProvider의 xAccessor (optional)
 * @returns [renderNodes, setRenderNodes] - State and setter, same as useState
 *
 * @example
 * const [renderNodes, setRenderNodes] = useLocalStorageShapes(
 *   "shapes_BTC/USDT:USDT_1h",
 *   {},
 *   candleData,
 *   "1h",
 *   xAccessor,
 * );
 */
export function useLocalStorageShapes(
  key: string,
  initialValue: RenderNodesMap = {},
  candleData: StockData[] = [],
  timeframe: string = '',
  xAccessor?: XAccessor,
): [RenderNodesMap, React.Dispatch<React.SetStateAction<RenderNodesMap>>] {
  const prevKeyRef = useRef<string>(key);
  const isFirstRender = useRef(true);

  // Load initial value from localStorage
  const getStoredValue = useCallback(
    (storageKey: string): RenderNodesMap => {
      // Check if running in browser (not SSR)
      if (typeof window === 'undefined') {
        return initialValue;
      }

      try {
        const item = localStorage.getItem(storageKey);
        if (item) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const parsed = JSON.parse(item) as StorageShapesInputMap;
          return restoreFromStorage(parsed, candleData, timeframe, xAccessor);
        }
      } catch (error) {
        console.warn(`Error loading renderNodes from localStorage key "${storageKey}":`, error);
      }
      return initialValue;
    },
    [initialValue, candleData, timeframe, xAccessor],
  );

  // Initialize state with localStorage value
  const [state, setState] = useState<RenderNodesMap>(() => {
    return getStoredValue(key);
  });

  // Handle key changes
  useEffect(() => {
    if (prevKeyRef.current !== key) {
      const value = getStoredValue(key);
      setState(value);
      prevKeyRef.current = key;
    }
  }, [key, getStoredValue]);

  // Save to localStorage with debounce to avoid excessive writes during drag operations
  useEffect(() => {
    // Skip if running on server (SSR)
    if (typeof window === 'undefined') {
      return undefined;
    }

    // Skip saving on first render (already loaded from localStorage)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }

    // Only save if key hasn't changed
    if (prevKeyRef.current !== key) {
      return undefined;
    }

    // Debounce localStorage writes to improve performance during rapid updates (e.g., dragging shapes)
    const saveTimer = setTimeout(() => {
      // 방어: candleData / xAccessor 미준비 상태에서의 저장 skip.
      // index → timestamp 좌표 변환을 수행할 수 없으므로, 저장을 건너뛰지 않으면
      // scale index 값(음수 포함)이 `coordinates` 에 그대로 저장되어 이후 F5 복원 시
      // 차트 영역 밖에 렌더링되는 회귀가 발생한다 (2026-04-22 발견, engview 통합 지점 사례).
      // 저장을 skip 하면 기존 유효한 localStorage 데이터가 보존되며, 이 hook 을 라이브러리로
      // 사용하는 환경(예: engview)에서 candleData 를 아예 전달하지 않는 경우 localStorage
      // 경로가 자연스럽게 비활성화되어 오염이 원천 차단된다.
      if (candleData.length === 0 || !xAccessor) {
        console.debug(
          `[useLocalStorageShapes] skip save for "${key}": ` +
            `candleData.length=${candleData.length}, xAccessor=${xAccessor ? 'present' : 'absent'}`,
        );
        return;
      }
      try {
        const sanitizedData = sanitizeForStorage(state);
        console.debug(`[useLocalStorageShapes] saving to "${key}":`, Object.keys(sanitizedData).length, 'charts');
        localStorage.setItem(key, JSON.stringify(sanitizedData));
      } catch (error) {
        console.warn(`Error saving renderNodes to localStorage key "${key}":`, error);
      }
    }, 500); // Wait 500ms after last change before saving

    // Cleanup: cancel pending save if state changes again or component unmounts
    return () => clearTimeout(saveTimer);
  }, [candleData.length, key, state, xAccessor]);

  return [state, setState];
}
